<script setup lang="ts">
import { computed, nextTick } from 'vue'
import { useVuelidate } from '@vuelidate/core'
import { required } from '@vuelidate/validators'
import { useUmsokn } from '@/composables/Applications/UseUmsokn'
import DatePicker from '../../Shared/DatePicker.vue'

interface CareerItem {
  name: string
  employeeName: string
  from: string | null
  to: string | null
}

const props = defineProps<{ disable?: boolean }>()

const { umsokn, onLoaded } = useUmsokn()

// 🧩 Dynamic validation rules for each list item
const rules = computed(() => ({
  umsokn: {
    career: Array.isArray(umsokn.value?.career)
      ? umsokn.value.career.map(() => ({
          name: { required },
          employeeName: { required },
          from: { required },
          to: { required }
        }))
      : []
  }
}))

const v$ = useVuelidate(rules, { umsokn })

// ✅ Ensure one blank entry exists and reset validation on load
onLoaded(() => {
  if (!Array.isArray(umsokn.value.career) || umsokn.value.career.length === 0) {
    umsokn.value.career = [{ name: '', employeeName: '', from: null, to: null }]
  }
  nextTick(() => v$.value.$reset())
})

// ✅ Methods
function addChild() {
  umsokn.value?.career.push({ name: '', employeeName: '', from: null, to: null })
}
function removeChild(index: number) {
  umsokn.value?.career.splice(index, 1)
  if (umsokn.value.career.length === 0) {
    umsokn.value.career.push({ name: '', employeeName: '', from: null, to: null })
  }
}
function todayISO() {
  return new Date().toISOString().split('T')[0]
}

// ✅ Validation exposure
defineExpose({
  async validate() {
    if (Array.isArray(umsokn.value.career)) {
      umsokn.value.career.forEach((_: CareerItem, i: number) => {
        v$.value.umsokn.career[i].$touch()
      })
    }
    const isValid = await v$.value.$validate()
    return !v$.value.$invalid && isValid
  }
})
</script>

<template>
  <div v-if="umsokn">
    <v-col>
      <div class="forms__header">{{ $t('ororku_umsokn_step6_header') }}</div>

      <transition-group name="list">
        <div
          v-for="(item, index) in umsokn.career"
          :key="'career_' + index"
          class="list-item"
          style="margin: 20px 0;"
        >
          <div v-if="index > 0" class="forms__divider__container">
            <div class="forms__divider"></div>
            <button
              v-if="!props.disable"
              class="forms__warning__list__wrapper"
              @click="removeChild(index)"
            >
              <img class="forms__warning__icon" src="@/assets/icons/Close.svg" />
            </button>
          </div>

          <v-row>
            <!-- Job title -->
            <v-col cols="12" md="6">
              <v-text-field
                v-model="item.name"
                variant="outlined"
                density="comfortable"
                :readonly="props.disable"
                :label="$t('ororku_umsokn_step6_starfsheiti')"
                :error="v$.umsokn.career[index]?.name?.$error"
                :error-messages="v$.umsokn.career[index]?.name?.$dirty
                  ? [
                      v$.umsokn.career[index]?.name?.required?.$invalid
                        ? $t('ororku_umsokn_step6_starfsheiti_error')
                        : ''
                    ].filter(Boolean)
                  : []"
              />
            </v-col>

            <!-- Employer -->
            <v-col cols="12" md="6">
              <v-text-field
                v-model="item.employeeName"
                variant="outlined"
                density="comfortable"
                :readonly="props.disable"
                :label="$t('ororku_umsokn_step6_vinnuveitandi')"
                :error="v$.umsokn.career[index]?.employeeName?.$error"
                :error-messages="v$.umsokn.career[index]?.employeeName?.$dirty
                  ? [
                      v$.umsokn.career[index]?.employeeName?.required?.$invalid
                        ? $t('ororku_umsokn_step6_vinnuveitandi_error')
                        : ''
                    ].filter(Boolean)
                  : []"
              />
            </v-col>
          </v-row>

          <v-row>
            <!-- From -->
            <v-col cols="12" md="6">
              <DatePicker
                v-model="item.from"
                :label="$t('ororku_umsokn_step6_from')"
                :disabled="props.disable"
                :max="todayISO()"
                :error="v$.umsokn.career[index]?.from?.$error"
                :error-messages="v$.umsokn.career[index]?.from?.$dirty
                  ? [
                      v$.umsokn.career[index]?.from?.required?.$invalid
                        ? $t('ororku_umsokn_step6_from_error')
                        : ''
                    ].filter(Boolean)
                  : []"
              />
            </v-col>

            <!-- To -->
            <v-col cols="12" md="6">
              <DatePicker
                v-model="item.to"
                :label="$t('ororku_umsokn_step6_to')"
                :disabled="props.disable"
                :max="todayISO()"
                :error="v$.umsokn.career[index]?.to?.$error"
                :error-messages="v$.umsokn.career[index]?.to?.$dirty
                  ? [
                      v$.umsokn.career[index]?.to?.required?.$invalid
                        ? $t('ororku_umsokn_step6_to_error')
                        : ''
                    ].filter(Boolean)
                  : []"
              />
            </v-col>
          </v-row>
        </div>
      </transition-group>

      <div class="m-t-40" v-if="!props.disable">
        <v-btn
          color="primary"
          variant="outlined"
          prepend-icon="mdi-plus"
          class="mt-4"
          @click="addChild"
        >
          {{ $t('ororku_umsokn_step3_add') }}
        </v-btn>
      </div>
    </v-col>
  </div>
</template>

<style scoped lang="scss">

</style>
